import type { FmMediaAsset, FmMediaProvenance } from './mediaRegistry';

/**
 * Media published to the public lehti-media catalog without bytes in Git
 * (docs/media-delivery.md). Every object is content-addressed and immutable:
 * stills are the WebP derivatives scripts/media/publish.py wrote, loops are
 * video packages whose manifest records sha256, bytes and contentType. A
 * changed render is a new object key, never an overwrite.
 */
export const LEHTI_MEDIA_CATALOG_BASE = 'https://2u7fo.upcloudobjects.com/lehti-media/catalog/v1';

const RETRIEVED_AT = '2026-10-13T20:30:00Z';

function catalogStill(identity: string, small: string, large: string) {
  const url = (hash: string, width: 640 | 1280) =>
    `${LEHTI_MEDIA_CATALOG_BASE}/${identity}/${hash}-${width}.webp`;
  return { src: url(large, 1280), srcSet: `${url(small, 640)} 640w, ${url(large, 1280)} 1280w` };
}

function catalogLoop(packageIdentity: string, release: string, rendition: 'master' | 'mobile') {
  return `${LEHTI_MEDIA_CATALOG_BASE}/${packageIdentity}/${release}/neutral/${rendition}.mp4`;
}

const SENTINEL_SCENES = [
  'S2B_T35VNJ_20180731T093030_L2A',
  'S2A_T35VNJ_20200615T093040_L2A',
  'S2A_T35VNJ_20210630T093134_L2A',
  'S2B_T35VNJ_20230807T093841_L2A',
  'S2B_T35VNJ_20240722T094244_L2A',
  'S2B_T35VNJ_20250717T094033_L2A',
  'S2B_T35VNJ_20260622T094028_L2A',
] as const;

const SENTINEL_PACKAGE = 'landscape.sentinel-timelapse';
const SENTINEL_RELEASE = '2026-10-13-bb3c9f79dd67';
const STAND_MAP_PACKAGE = 'property.stand-map';
const STAND_MAP_RELEASE = '2026-10-13-5e2e8d361093';

const sentinelProvenance: FmMediaProvenance = {
  source: 'skogiq-data-render',
  createdAt: RETRIEVED_AT,
  creator: 'Lehti AI (render of Copernicus Sentinel-2 L2A true colour)',
  sourceUrl: 'https://earth-search.aws.element84.com/v1/collections/sentinel-2-c1-l2a',
  licenseUrl: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  retrievedAt: RETRIEVED_AT,
  licenseVersion:
    "Legal notice on the use of Copernicus Sentinel Data and Service Information (free, full and open access; credit 'Contains modified Copernicus Sentinel data <year>'), retrieved 2026-10-13",
  dataDependencies: [
    ...SENTINEL_SCENES.map((scene) => `sentinel-2-l2a:${scene}`),
    'region:around-demonstration-property-178-415-5-2',
    `package:${LEHTI_MEDIA_CATALOG_BASE}/${SENTINEL_PACKAGE}/${SENTINEL_RELEASE}/manifest.json`,
  ],
  replacementPolicy: 'stable',
};

const orthoProvenance: FmMediaProvenance = {
  source: 'skogiq-data-render',
  createdAt: RETRIEVED_AT,
  creator: 'Lehti AI (render of National Land Survey of Finland orthophoto)',
  sourceUrl:
    'https://www.maanmittauslaitos.fi/en/maps-and-spatial-data/datasets-and-interfaces/product-descriptions/orthophotos',
  licenseUrl: 'https://creativecommons.org/licenses/by/4.0/',
  retrievedAt: RETRIEVED_AT,
  licenseVersion:
    'NLS Finland open data licence CC BY 4.0 (https://www.maanmittauslaitos.fi/en/opendata-licence-cc40), retrieved 2026-10-13',
  dataDependencies: ['mml:ortokuva:WGS84-bbox:28.0115,61.7965,28.1050,61.8225:z16'],
  replacementPolicy: 'stable',
};

const standMapProvenance: FmMediaProvenance = {
  ...orthoProvenance,
  creator: 'Lehti AI (stand boundaries and volumes over the NLS Finland orthophoto)',
  licenseVersion:
    'NLS Finland orthophoto and Finnish Forest Centre open forest data, both CC BY 4.0, retrieved 2026-10-13',
  dataDependencies: [
    'property:178-415-5-2',
    'metsakeskus:open-forest-data:stands',
    'mml:ortokuva:z16',
    `package:${LEHTI_MEDIA_CATALOG_BASE}/${STAND_MAP_PACKAGE}/${STAND_MAP_RELEASE}/manifest.json`,
  ],
  replacementPolicy: 'regenerate-on-data-change',
};

const SENTINEL_CREDIT =
  'Illustrative landscape · Contains modified Copernicus Sentinel data 2018–2026';
const ORTHO_CREDIT =
  'Illustrative aerial view · Orthophoto © National Land Survey of Finland, CC BY 4.0';
const STAND_MAP_CREDIT =
  'Demonstration property · Stands © Finnish Forest Centre, orthophoto © NLS Finland, CC BY 4.0';

const BASE = { requiresCredit: true, webglPolicy: { webglAllowed: false } } as const;

export const PUBLISHED_MEDIA_REGISTRY: Record<string, FmMediaAsset> = {
  'landscape.sentinel-timelapse.loop-wide': {
    ...BASE,
    key: 'landscape.sentinel-timelapse.loop-wide',
    kind: 'video',
    src: catalogLoop(SENTINEL_PACKAGE, SENTINEL_RELEASE, 'master'),
    poster: 'landscape.sentinel-timelapse.poster-wide',
    mobileVariant: 'landscape.sentinel-timelapse.loop-mobile',
    alt: 'Sentinel-2 summer images of a lake-rich Finnish forest landscape from 2018 to 2026, fading from year to year',
    credit: SENTINEL_CREDIT,
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'green',
    autoplay: true,
    loop: true,
    provenance: sentinelProvenance,
    webglPolicy: {
      webglAllowed: false,
      reducedMotionFallback: 'landscape.sentinel-timelapse.poster-wide',
    },
  },
  'landscape.sentinel-timelapse.loop-mobile': {
    ...BASE,
    key: 'landscape.sentinel-timelapse.loop-mobile',
    kind: 'video',
    src: catalogLoop(SENTINEL_PACKAGE, SENTINEL_RELEASE, 'mobile'),
    poster: 'landscape.sentinel-timelapse.poster-mobile',
    alt: 'Narrow crop of Sentinel-2 summer images of a Finnish forest landscape from 2018 to 2026',
    credit: SENTINEL_CREDIT,
    aspect: 'portrait',
    focalPoint: 'center',
    dominantTone: 'green',
    autoplay: true,
    loop: true,
    provenance: sentinelProvenance,
    webglPolicy: {
      webglAllowed: false,
      reducedMotionFallback: 'landscape.sentinel-timelapse.poster-mobile',
    },
  },
  'landscape.sentinel-timelapse.poster-wide': {
    ...BASE,
    key: 'landscape.sentinel-timelapse.poster-wide',
    kind: 'poster',
    ...catalogStill(
      'landscape.sentinel-timelapse.poster-wide',
      '4794986b9c1d676a97284f0c6c21a2b86e10ea5c43c15ff972e265accddf13df',
      'b09a482825ef870f1e32f5a04af91892a1db5d76654392d5e4f7baee9f07ccee',
    ),
    mobileVariant: 'landscape.sentinel-timelapse.poster-mobile',
    alt: 'Sentinel-2 summer image of a lake-rich Finnish forest landscape in 2026',
    credit: SENTINEL_CREDIT,
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'green',
    provenance: sentinelProvenance,
  },
  'landscape.sentinel-timelapse.poster-mobile': {
    ...BASE,
    key: 'landscape.sentinel-timelapse.poster-mobile',
    kind: 'poster',
    ...catalogStill(
      'landscape.sentinel-timelapse.poster-mobile',
      '89e9be1778dcd735c0ba6e21b3b9b3778aa2d181bad0c7b0e65e7a75d641fa2e',
      '4d881e1c21d03a9618c510b7609cc44892cce440435cf747ecee7fa596c8a46b',
    ),
    alt: 'Narrow crop of a Sentinel-2 summer image of a Finnish forest landscape in 2026',
    credit: SENTINEL_CREDIT,
    aspect: 'portrait',
    focalPoint: 'center',
    dominantTone: 'green',
    provenance: sentinelProvenance,
  },
  'landscape.aerial-ortho.overview': {
    ...BASE,
    key: 'landscape.aerial-ortho.overview',
    kind: 'map-poster',
    ...catalogStill(
      'landscape.aerial-ortho.overview',
      '9856eb4220c33c55bf189a87143ac6c64883e3ef6e1b9cd7eed6c6c0f3dd5ed0',
      '0f35ec68bf0d586b5eb3e9f85285910ca83454df3fb8794acba0b0e462bca582',
    ),
    mobileVariant: 'landscape.aerial-ortho.overview-mobile',
    alt: 'Aerial orthophoto of Finnish forest stands, lakes and fields',
    credit: ORTHO_CREDIT,
    aspect: 'wide',
    focalPoint: '48% 45%',
    dominantTone: 'dark',
    provenance: orthoProvenance,
    cropProfiles: {
      background: {
        aspect: 'wide',
        focalPoint: '48% 45%',
        note: 'Keep forest texture and the lake edge behind the copy',
      },
      split: {
        aspect: 'wide',
        focalPoint: '48% 45%',
        note: 'Keep the forest mosaic and lakes in frame',
      },
    },
  },
  'landscape.aerial-ortho.overview-mobile': {
    ...BASE,
    key: 'landscape.aerial-ortho.overview-mobile',
    kind: 'map-poster',
    ...catalogStill(
      'landscape.aerial-ortho.overview-mobile',
      'a7c232f2f41bd0c28bdba180f9a04d1746e70858ebf01c9a3cc4fd176239ee83',
      'e046d3231906da13a66f51ecc83024d9108c89877cc6d2734ef4c61f22f87307',
    ),
    alt: 'Narrow aerial orthophoto crop of Finnish forest stands and a lake',
    credit: ORTHO_CREDIT,
    aspect: 'portrait',
    focalPoint: 'center',
    dominantTone: 'dark',
    provenance: orthoProvenance,
  },
  'property.stand-map.reveal-wide': {
    ...BASE,
    key: 'property.stand-map.reveal-wide',
    kind: 'animation',
    src: catalogLoop(STAND_MAP_PACKAGE, STAND_MAP_RELEASE, 'master'),
    poster: 'property.stand-map.poster-wide',
    mobileVariant: 'property.stand-map.reveal-mobile',
    alt: "A demonstration property's forest stands appear one by one over an aerial orthophoto, shaded by growing stock volume",
    credit: STAND_MAP_CREDIT,
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'dark',
    autoplay: true,
    loop: true,
    provenance: standMapProvenance,
    webglPolicy: { webglAllowed: false, reducedMotionFallback: 'property.stand-map.poster-wide' },
  },
  'property.stand-map.reveal-mobile': {
    ...BASE,
    key: 'property.stand-map.reveal-mobile',
    kind: 'animation',
    src: catalogLoop(STAND_MAP_PACKAGE, STAND_MAP_RELEASE, 'mobile'),
    poster: 'property.stand-map.poster-mobile',
    alt: "Narrow view of a demonstration property's forest stands appearing over an aerial orthophoto",
    credit: STAND_MAP_CREDIT,
    aspect: 'portrait',
    focalPoint: 'center',
    dominantTone: 'dark',
    autoplay: true,
    loop: true,
    provenance: standMapProvenance,
    webglPolicy: { webglAllowed: false, reducedMotionFallback: 'property.stand-map.poster-mobile' },
  },
  'property.stand-map.poster-wide': {
    ...BASE,
    key: 'property.stand-map.poster-wide',
    kind: 'poster',
    ...catalogStill(
      'property.stand-map.poster-wide',
      '1d4ce0393985ce41b732cd55c76125a6477051485de4b25e025ae853f32ee7a6',
      'e22b558185ccd03ef7fcc7a358584a583ff489e6f0392a560671ba7ff02ff802',
    ),
    mobileVariant: 'property.stand-map.poster-mobile',
    alt: 'Stand boundaries of a demonstration forest property over an aerial orthophoto, shaded by growing stock volume',
    credit: STAND_MAP_CREDIT,
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'dark',
    provenance: standMapProvenance,
  },
  'property.stand-map.poster-mobile': {
    ...BASE,
    key: 'property.stand-map.poster-mobile',
    kind: 'poster',
    ...catalogStill(
      'property.stand-map.poster-mobile',
      'fad8174a9f1e15b76a6415a7cd720c491adb09a3d8c3604ee8b600b12a0034b3',
      '31e4a300c5199fe5e7af74c9490d8e207f808a745ab504593f5a75908886d3c6',
    ),
    alt: "Narrow view of a demonstration property's stand boundaries over an aerial orthophoto",
    credit: STAND_MAP_CREDIT,
    aspect: 'portrait',
    focalPoint: 'center',
    dominantTone: 'dark',
    provenance: standMapProvenance,
  },
};
